import { DOCUMENT_FORMAT_MIME_TYPE, claimedDocumentFormat, isOpaqueUploadMimeType, isReadableDocumentFormat, normalizeMimeType } from '@numeo/types-core'

const decodeUrlForTypeInference = (url: string): string => {
    try {
        return decodeURIComponent(url)
    } catch {
        return url
    }
}

const fileNameCandidatesForTypeInference = (url: string, title: string): string[] => {
    const decodedUrl = decodeUrlForTypeInference(url)
    try {
        const parsedUrl = new URL(decodedUrl)
        return [parsedUrl.pathname, decodedUrl.split(/[?#]/)[0] || '', title]
    } catch {
        return [decodedUrl.split(/[?#]/)[0] || '', title]
    }
}

const inferPreviewMimeType = (serverMimeType: string, url: string, title: string): string => {
    for (const fileName of fileNameCandidatesForTypeInference(url, title)) {
        const format = claimedDocumentFormat(serverMimeType, fileName)
        if (isReadableDocumentFormat(format)) return DOCUMENT_FORMAT_MIME_TYPE[format]
    }
    return ''
}

export const getPreviewMimeType = (url: string, title: string, serverMimeType: string): string => {
    const normalizedServerMimeType = normalizeMimeType(serverMimeType)
    if (normalizedServerMimeType && !isOpaqueUploadMimeType(normalizedServerMimeType)) return normalizedServerMimeType

    return inferPreviewMimeType(normalizedServerMimeType, url, title) || normalizedServerMimeType
}

export const normalizePreviewBlob = (blob: Blob, url: string, title: string, downloadFilename?: string): Blob => {
    const previewMimeType = getPreviewMimeType(url, title, blob.type)
    const normalizedBlob = previewMimeType && previewMimeType !== blob.type ? new Blob([blob], { type: previewMimeType }) : blob
    const trimmedFilename = downloadFilename?.trim()

    return trimmedFilename ? new File([normalizedBlob], trimmedFilename, { type: normalizedBlob.type }) : normalizedBlob
}

// The zoom contract lives with the shared viewer; re-exported so existing imports of this module keep working.
export { PDF_PREVIEW_DEFAULT_ZOOM, PDF_PREVIEW_MAX_ZOOM, PDF_PREVIEW_MIN_ZOOM, PDF_PREVIEW_ZOOM_STEP } from '@/components/shared/document-viewer/previewZoom'

const PDF_POINT_TO_CSS_PIXEL_SCALE = 96 / 72

export const getPdfRenderScale = (zoom: number): number => zoom * PDF_POINT_TO_CSS_PIXEL_SCALE

export const getPdfFitWidth = ({
    containerWidth,
    containerHeight,
    pageWidth,
    pageHeight,
    zoom,
}: {
    containerWidth: number
    containerHeight: number
    pageWidth: number
    pageHeight: number
    zoom: number
}): number => {
    const availableWidth = Math.max(containerWidth - 32, 1)
    const availableHeight = Math.max(containerHeight - 48, 1)
    return Math.min(availableWidth, availableHeight * (pageWidth / pageHeight)) * zoom
}

/**
 * The screen's pixel ratio, capped at 2: sharp on any screen, and a phone's 3× would draw each page with more than
 * twice the pixels, which runs iOS Safari out of canvas memory on a long PDF.
 */
export const getCrispCanvasRatio = (): number => {
    if (typeof window === 'undefined') return 2
    return Math.min(Math.max(window.devicePixelRatio || 1, 1), 2)
}
